/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Action, AnyAction, Dispatch, MiddlewareAPI } from 'redux';
import {
  PauseGameCallback,
  StartGameCallback,
  StopGameCallback,
  UserAddedCallback
} from '../../lib/copilotService/CopilotCallbacks';
import { CopilotService } from '../../lib/copilotService/CopilotService';
import { CopilotServiceCallbacks } from '../../lib/copilotService/CopilotServiceCallbacks';
import { ContentType, Difficulty, ContentSetup, ModuleInfo } from '../../utils';
import {
  activityStart,
  activityStop,
  userAdded,
  widgetReady
} from '../actions/embedding';
import {
  progressStateLoaded,
  nextPageTypeAction,
  incrementCorrectAnswer
} from '../actions/progression';
import { endOfModuleSurveySelected, requestReviewStudentResponses, submitResponse } from '../actions/ui';
import { getContentTypeFromSetup, safelyUnpackJson } from '../../utils/utils';
import { ITSEventType, SFFProblemType } from 'its-pages';
import type { State } from '../state';

const initializeCopilotService = <S extends Dispatch<AnyAction>>(store: MiddlewareAPI<S>) => {
  const onStart: StartGameCallback = (
    onComplete,
    activityDefinition,
    details
  ) => {
    const moduleInfo: ModuleInfo = activityDefinition.activityData;
    const savedUserStateJson = details?.lessonState;
    const noLogging = details?.noLogging;
    const savedUserState = safelyUnpackJson(savedUserStateJson);
    const activityId = activityDefinition.daid;
    const { cid } = activityDefinition;

    store.dispatch(
      activityStart({
        moduleInfo,
        activityId,
        cid,
        savedUserState,
        noLogging
      })
    );
    onComplete(true);
  };

  const onStop: StopGameCallback = (onComplete) => {
    // eslint-disable-next-line no-console
    console.log('Received StopGame');
    store.dispatch(activityStop());
    onComplete(true);
  };

  const onPause: PauseGameCallback = (onComplete) => {
    onComplete(true);
  };
  const onUserAdded: UserAddedCallback = (onComplete, user) => {
    store.dispatch(userAdded(user));
    onComplete(true);
  };

  const noOp = () => ({});

  const copilotServiceCallbacks = new CopilotServiceCallbacks(
    onStart,
    onStop,
    onPause,
    onUserAdded,
    noOp,
    noOp
  );

  return new CopilotService(copilotServiceCallbacks);
};

/**
 *  The function that listens for and dispatches actions
 *  relevant to the embedding API.
 */
 const embeddingMiddleware = <D extends Dispatch<AnyAction>>(
  store: MiddlewareAPI<D, State>,
  copilotService: CopilotService
) => (next: D) => <A extends Action>(action: A) => {
  // (boilerplate) Call the next dispatch method in the middleware chain.
  const returnValue = next(action);

  if (widgetReady.match(action)) {
    // Inform the service that the app (game) is ready to communicate
    // with the student client
    copilotService.onWidgetReady();
  }

  const state = store.getState();
  const { userId } = state.ui;

  if (userId === null) {
    return returnValue;
  }

  if (submitResponse.match(action)) {
    // Current Problem Reducer already had a chance to processed the payload
    // so responses have been evaluated and score - calculated

    if (state.currentProblemState.pageContent) {
      const { pageContent } = state.currentProblemState;
      const pageData = pageContent.pageData;

      pageData.recordEvent(ITSEventType.problemSubmit);
      const rawData = pageData.rawData;
      const problemData = {
        gname: 'its-psych',
        pageData: rawData
      };

      const score = rawData.playStats ? rawData.playStats.score : 0;

      store.dispatch(incrementCorrectAnswer(score > 0.95 ? 1 : 0));

      // HACK: A submit is fired when the module intro AND module completion screens
      // are displayed
      const problemId = Date.now().toString();
      copilotService.logProblemResult(userId, score, problemId, problemData);

      // If a module does not have a survey at the end, then the display of the module completion screen
      // should also send a signal to assign a grade. Otherwise wait for the survey link click
      if (pageData.type === SFFProblemType.ModuleCompletion) {
        // !getEndOfModuleSurveyExists(pageData.skill.name)
        // Always send grade as soon as the module complete screen is reached
        copilotService.logModuleComplete(userId, true);
      }
    }
  } else if (endOfModuleSurveySelected.match(action)) {
    // In some cases, use must click on the end of module survey to get full credit (i.e. don't submit
    // a canvas grade until they click the survey link)
    copilotService.moduleSurveyClicked(userId);
  } else if (requestReviewStudentResponses.match(action)) {
    copilotService.requestReviewStudentResponses(userId);
  }

  const sendSetStart = (
    problemType: ContentType,
    progressionIndex: number,
    difficulty: Difficulty
  ) => {
    if (
      problemType !== ContentType.ModuleIntro &&
      problemType !== ContentType.ModuleCompletion
    ) {
      const {
        currentProblemState: { setup },
        progression: { isResumed }
      } = state;

      if (setup !== undefined) {
        const {
          moduleInfo: {
            skill: { name }
          }
        } = setup;

        copilotService.logProblemSetStart(
          userId,
          `${name}-${progressionIndex}-${difficulty.secondary + 1}`,
          // problemCount
          -1,
          { skill: name },
          { resume: isResumed }
        );
      }
    }
  };

  if (nextPageTypeAction.match(action)) {
    const setup: ContentSetup = action.payload;
    sendSetStart(
      getContentTypeFromSetup(setup),
      setup.progressionIndex,
      setup.difficulty
    );
  }

  if (progressStateLoaded.match(action)) {
    const {
      currentProblemState: { setup }
    } = state;
    if (setup !== undefined) {
      sendSetStart(
        getContentTypeFromSetup(setup),
        setup.progressionIndex,
        setup.difficulty
      );
    }
  }

  // (boilerplate) This will likely be the action itself, unless
  // a middleware further in chain changed it.
  return returnValue;
};

/**
 * Create middleware that handles actions related to the Embedding API
 * (which allows running games inside the Copilot Student Client)
 * @param store Redux store
 */

export let cpsRef: CopilotService;
export const createEmbeddingMiddleware = <
  D extends Dispatch<AnyAction>
>(
  store: MiddlewareAPI<D>
) => {
  // Initialize the API service
  const copilotService = initializeCopilotService(store);

  cpsRef = copilotService;
  // Return the function that listens for and dispatches actions
  // relevant to the embedding API.
  return embeddingMiddleware(store, copilotService);
};
